import * as React from 'react'
import { cn } from '~/lib/utils'
import { FormItem, FormLabel } from '~/components/ui/form'

/**
 * Geometry of a `FormGrid` cell, in one place.
 *
 * A `FormItem` is a plain block, so its label, control and message stack on their
 * own. Two cells side by side therefore drift apart as soon as one label wraps to
 * a second line: the wrapping cell pushes only its own control down. Grid
 * stretches the cells to equal height, not their contents.
 *
 * `row-span-2` + `grid-rows-[subgrid]` makes the cell hand its two children to the
 * PARENT grid's row tracks, so every cell in a row shares one label track and one
 * control track — the tallest label sets the height for all of them and the
 * controls line up. An error message grows the shared control track, so a field
 * showing an error does not shift its neighbour either.
 *
 * Consumers must therefore render EXACTLY two children: the label, and one
 * element wrapping the control together with its message. A third child would
 * land outside the spanned tracks.
 *
 * Outside a grid both classes are inert and this lays out as a plain stack, which
 * is why the same cell works in and out of `FormGrid`.
 *
 * Callers add their own `gap-y-*`: a subgrid overrides the gutters it inherits
 * from the parent, so this is what sets label-to-control spacing.
 */
export const formGridCell = 'row-span-2 grid grid-rows-[subgrid]'

export type FormFieldCellProps = {
  label: React.ReactNode
  required?: boolean
  // Renders the ⓘ tooltip beside the label.
  description?: string
  // Turn the label red only once the field is touched — see `FormLabel`.
  requireTouched?: boolean
  className?: string
  // The control and its message. Kept together so they share the control track.
  children: React.ReactNode
}

/**
 * A labelled `FormGrid` cell: label on the shared label row, control + message on
 * the shared control row. Use this instead of assembling `FormItem` by hand, so a
 * wrapping label can never knock a field out of alignment with its neighbour.
 *
 * Group controls (`StyledRadioGroup`, `StyledCheckboxField`) build the same cell
 * from `formGridCell` directly, because they need a `fieldset`/`legend` rather
 * than a `label`.
 */
export const FormFieldCell = ({
  label,
  required,
  description,
  requireTouched,
  className,
  children,
}: FormFieldCellProps) => (
  // `items-start`: grid items stretch by default, so in a row whose label track is
  // two lines tall a one-line label would be centred against it (most visibly with
  // `description`, which wraps the label in a `flex items-center` row). Start-aligning
  // both children keeps every label's first line — and every input's top edge — on
  // the top edge of its track.
  // `space-y-0`: FormItem's default margins would stack on top of the row gap.
  <FormItem className={cn(formGridCell, 'items-start gap-y-2 space-y-0', className)}>
    <FormLabel required={required} description={description} requireTouched={requireTouched}>
      {label}
    </FormLabel>
    <div className="space-y-2">{children}</div>
  </FormItem>
)
